import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Drag&drop hook for the operators matrix. Returns handlers + state needed
 * to wire a `<tr>`/`<td>` grid where chips can be moved between cells of the
 * SAME operator. Crossing operators is intentionally blocked (use ReassignMenu).
 */
export function useMatrixDragDrop(onReassigned) {
  const [draggingChip, setDraggingChip] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);

  const handleDragStart = (e, ev, operatorId, fromIso) => {
    setDraggingChip({ ev, operatorId, fromIso });
    e.dataTransfer.effectAllowed = "move";
    try {
      // For Firefox compatibility — must set some data
      e.dataTransfer.setData("text/plain", ev.doc_id);
    } catch (err) {
      // Alcuni browser bloccano setData fuori da gesture utente; il drag funziona comunque.
      console.warn("[OperatorsMatrix] dataTransfer.setData ignorato:", err?.message);
    }
  };

  const handleDragEnd = () => {
    setDraggingChip(null);
    setDragOverCell(null);
  };

  const handleDragOver = (e, operatorId, isoDate) => {
    if (!draggingChip) return;
    if (draggingChip.operatorId !== operatorId) return;
    if (draggingChip.fromIso === isoDate) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCell(`${operatorId}-${isoDate}`);
  };

  const handleDragLeaveCell = (cellKey) => {
    setDragOverCell((prev) => (prev === cellKey ? null : prev));
  };

  const handleDrop = async (e, operatorId, isoDate) => {
    e.preventDefault();
    if (!draggingChip) return;
    const { ev, operatorId: fromOp, fromIso } = draggingChip;
    setDragOverCell(null);
    setDraggingChip(null);
    if (operatorId !== fromOp) {
      toast.info("Per riassegnare a un altro operatore usa il menu Riassegna sul chip");
      return;
    }
    if (fromIso === isoDate) return;
    try {
      await api.put(`/calendar/events/${ev.doc_id}`, null, {
        params: { event_type: ev.eventType, new_date: isoDate },
      });
      toast.success("Data aggiornata");
      onReassigned?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile spostare l'evento");
    }
  };

  return {
    draggingChip,
    dragOverCell,
    handleDragStart, handleDragEnd, handleDragOver, handleDragLeaveCell, handleDrop,
  };
}
